<template>
  <div class="microbiology-results width-responsiveness">
    <div
      v-if="loading"
      class="loading-container"
    >
      <Spinner />
      <p>Carregando resultados...</p>
    </div>

    <div
      v-else-if="results.length === 0"
      class="no-results"
    >
      <p>Nenhum resultado encontrado.</p>
    </div>

    <div v-else>
      <div class="results-header">
        <div class="filter-container">
          <div class="search">
            <input
              v-model="filterText"
              placeholder="Filtrar listagem..."
              class="search-box__input"
            />
            <img
              src="/static/images/icon-search-purple.svg"
              class="search-box__icon"
            />
          </div>
        </div>

        <h3>{{ filteredResults.length }} resultado(s) encontrado(s)</h3>
      </div>

      <DataCardTable
        :columns="columns"
        :data="filteredResults"
        :default-sort="{ key: 'patient', direction: 'asc' }"
        @sort-change="handleSortChange"
        @item-expanded="onItemExpanded"
      >
        <template #card-top-right="{ item }">
          <div
            v-if="isViewed(item)"
            class="viewed-badge"
          >
            <md-icon class="viewed-badge-icon">visibility</md-icon>
            <span>Visualizado</span>
          </div>
        </template>

        <template #cell-patient="{ value }">
          <span
            class="priority-text"
            :title="value"
          >
            {{ value || '-' }}
          </span>
        </template>

        <template #cell-exam="{ value }">
          <span
            class="priority-text"
            :title="value"
          >
            {{ value || '-' }}
          </span>
        </template>

        <template #cell-status="{ value }">
          <span
            class="status-badge"
            :class="getStatusClass(value)"
          >
            {{ value }}
          </span>
        </template>

        <template #cell-finalResult="{ value }">
          <span
            v-if="value"
            class="final-result-badge"
            :class="getFinalResultClass(value)"
          >
            <span class="final-result-icon">
              <span class="final-result-symbol">{{ getFinalResultSymbol(value) }}</span>
            </span>
            {{ value }}
          </span>
          <span
            v-else
            class="final-result-empty"
          >
            -
          </span>
        </template>

        <template #expanded-content="{ item }">
          <div class="expanded-details">
            <div class="exam-info-section">
              <div class="exam-info-grid">
                <div class="info-item">
                  <span class="label">Exame | Amostra</span>
                  <span class="value">
                    {{ item.exam }}{{ item.sampleId ? ` | ${item.sampleId}` : '' }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="label">Sítio</span>
                  <span class="value">{{ item.sitio || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Material</span>
                  <span class="value">{{ item.material }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Tempo de incubação</span>
                  <span class="value">{{ item.incubationTime || '-' }}</span>
                </div>
                <div class="info-item download-section">
                  <button
                    class="btn btn-primary btn-inline"
                    @click.stop="goToTraceability(item)"
                  >
                    <md-tooltip
                      md-delay="400"
                      md-direction="left"
                    >
                      Ir para rastreabilidade
                    </md-tooltip>
                    <md-icon
                      md-src="/static/images/traceability.svg"
                      class="icon-pdf traceability"
                    />
                  </button>

                  <button
                    class="btn btn-primary btn-inline"
                    :disabled="isReportUnavailable(item) || downloading === item.id"
                    @click.stop="downloadReport(item)"
                  >
                    <md-tooltip
                      md-delay="400"
                      md-direction="left"
                    >
                      Exportar laudo em PDF
                    </md-tooltip>
                    <md-icon
                      v-if="downloading !== item.id"
                      md-src="/static/images/icon-pdf-doc.svg"
                      class="icon-pdf"
                    />
                    <md-icon v-else>hourglass_empty</md-icon>
                  </button>

                  <button
                    class="btn btn-primary btn-inline"
                    :disabled="isReportUnavailable(item) || previewing === item.id"
                    @click.stop="viewReport(item)"
                  >
                    <md-tooltip
                      md-delay="400"
                      md-direction="left"
                    >
                      Ver laudo
                    </md-tooltip>
                    <md-icon
                      v-if="previewing !== item.id"
                      md-src="/static/images/icon-eye-alt.svg"
                      class="icon-download"
                    />
                    <md-icon v-else>hourglass_empty</md-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="timeline-section">
              <MicrobiologyProgressBar :progress-steps="item.progress" />
            </div>
          </div>
        </template>

        <template #empty-state>
          <p>Nenhum resultado encontrado.</p>
        </template>
      </DataCardTable>
    </div>
    <ResultsPDFAppendHTML />
  </div>
</template>

<script>
import Spinner from '@/components/common/Spinner'
import DataCardTable from '@/components/common/DataCardTable.vue'
import MicrobiologyProgressBar from './MicrobiologyProgressBar.vue'
import ResultsPDFAppendHTML from '@/components/ResultsPDF/ResultsPDFAppendHTML'
import microbiologyApi from '@/api/microbiology'
import resultsPDF from '@/api/resultsPDF'
import notify from '@/api/notification'
import { downloadFileByByteArray } from '@/utils/convertByteArrayToFile'

export default {
  name: 'MicrobiologyResults',
  components: {
    Spinner,
    DataCardTable,
    MicrobiologyProgressBar,
    ResultsPDFAppendHTML,
  },
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      downloading: null,
      previewing: null,
      filterText: '',
      viewedById: {},
      filteredResults: [],
      columns: [
        {
          key: 'orderId',
          title: 'OS ALVARO',
          sortable: true,
          width: '90px',
        },
        {
          key: 'orderIdLis',
          title: 'OS LIS',
          sortable: true,
          width: '105px',
        },
        {
          key: 'patient',
          title: 'PACIENTE',
          sortable: true,
          flex: 2.2,
        },
        {
          key: 'exam',
          title: 'EXAME',
          sortable: true,
          flex: 2,
        },
        {
          key: 'collectionDate',
          title: 'DATA DA COLETA',
          sortable: true,
          width: '12%',
        },
        {
          key: 'requestDate',
          title: 'DATA DA SOLICITAÇÃO',
          sortable: true,
          width: '12%',
        },
        {
          key: 'status',
          title: 'STATUS',
          sortable: true,
          width: '124px',
        },
        {
          key: 'finalResult',
          title: 'RESULTADO FINAL',
          sortable: true,
          width: '124px',
        },
      ],
    }
  },
  watch: {
    results: {
      immediate: true,
      handler(newResults) {
        this.filteredResults = this.normalizeResults(newResults)
        this.filterText = ''
      },
    },
    filterText() {
      this.filterResults()
    },
  },
  methods: {
    handleSortChange(sortConfig) {
      this.$emit('sort-change', sortConfig)
    },

    filterResults() {
      if (!this.filterText || !this.filterText.trim()) {
        this.filteredResults = this.normalizeResults(this.results)
        return
      }

      const searchTerm = this.filterText.toLowerCase().trim()
      this.filteredResults = this.normalizeResults(this.results).filter(result => {
        const patient = String(result.patient || '').toLowerCase()
        const orderId = String(result.orderId || '').toLowerCase()
        const orderIdLis = String(result.orderIdLis || '').toLowerCase()
        const exam = String(result.exam || '').toLowerCase()
        return (
          patient.includes(searchTerm) ||
          orderId.includes(searchTerm) ||
          orderIdLis.includes(searchTerm) ||
          exam.includes(searchTerm)
        )
      })
    },

    normalizeResults(results) {
      return (results || []).map(result => {
        const normalizedStatus = this.normalizeStatus(result.status)
        const finalResult = this.extractFinalResult(result, normalizedStatus)
        const wasViewed = !!(result && (result.viewed || this.viewedById[result.id]))

        if (result && result.id && wasViewed && !this.viewedById[result.id]) {
          this.$set(this.viewedById, result.id, true)
        }

        return {
          ...result,
          status: normalizedStatus,
          finalResult,
          viewed: wasViewed,
        }
      })
    },

    isViewed(item) {
      return !!(item && (item.viewed || this.viewedById[item.id]))
    },

    onItemExpanded({ id, expanded }) {
      if (!expanded || !id || this.viewedById[id]) {
        return
      }

      const expandedItem = this.filteredResults.find(item => item.id === id)
      if (expandedItem) {
        this.sendViewedTracking(expandedItem)
      }
    },

    async sendViewedTracking(item) {
      if (!item || !item.sampleId) return

      try {
        await microbiologyApi.markResultAsViewed([item.sampleId])
        this.$set(this.viewedById, item.id, true)
      } catch {}
    },

    normalizeStatus(status) {
      if (!status) return ''

      const parsedStatus = String(status || '').toLowerCase()

      if (parsedStatus.includes('andamento')) return 'Em andamento'
      if (parsedStatus.includes('finalizado')) return 'Finalizado'
      return ''
    },

    extractFinalResult(result, normalizedStatus) {
      return normalizedStatus === 'Finalizado' ? result.finalResult || '' : ''
    },

    getStatusClass(status) {
      switch (status) {
        case 'Finalizado':
          return 'status-completed'
        case 'Em andamento':
          return 'status-progress'
        default:
          return 'status-default'
      }
    },

    getFinalResultClass(finalResult) {
      const normalizedFinalResult = String(finalResult || '').toLowerCase()

      if (normalizedFinalResult.includes('positivo')) return 'final-result-positive'
      if (normalizedFinalResult.includes('negativo')) return 'final-result-negative'
      return 'final-result-default'
    },

    getFinalResultSymbol(finalResult) {
      const normalizedFinalResult = String(finalResult || '').toLowerCase()

      if (normalizedFinalResult.includes('positivo')) return '+'
      if (normalizedFinalResult.includes('negativo')) return '−'
      return '•'
    },

    isReportUnavailable(result) {
      return result.status !== 'Finalizado'
    },

    goToTraceability(result) {
      const url = `/traceability/search?q=${result.orderId}&typeOfSearch=OA`
      window.open(url, '_blank')
    },

    async downloadReport(result) {
      if (this.downloading === result.id) return

      this.downloading = result.id

      try {
        const response = await resultsPDF.downloadResultsPDF(
          result.orderId,
          result.examCode ? [result.examCode] : []
        )

        const fileName = `laudo_microbiologia_${result.orderId}_${result.examCode}.pdf`
        downloadFileByByteArray(response.data, fileName, 'application/pdf')

        notify({
          title: 'SUCESSO:',
          message: 'Laudo baixado com sucesso!',
        })
      } catch (error) {
        console.error('Erro ao baixar laudo:', error)
        notify({
          title: 'ERRO:',
          message: 'Erro ao baixar o laudo. Tente novamente.',
        })
      } finally {
        this.downloading = null
      }
    },

    async viewReport(result) {
      if (this.previewing === result.id) return

      this.previewing = result.id

      try {
        const response = await resultsPDF.downloadResultsPDF(
          result.orderId,
          result.examCode ? [result.examCode] : []
        )

        this.$store.commit('setHTMLForDownload', response.data)
        this.$store.commit('setResultsPdfModalStatus', true)
      } catch (error) {
        console.error('Erro ao visualizar laudo:', error)
        notify({
          title: 'ERRO:',
          message: 'Erro ao carregar o laudo. Tente novamente.',
        })
      } finally {
        this.previewing = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.microbiology-results {
  margin-top: 30px;
  font-family: 'Exo 2', sans-serif !important;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    gap: 16px;

    p {
      color: #666;
      font-size: 14px;
    }
  }

  .no-results {
    text-align: center;
    padding: 40px;
    color: #666;
  }

  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      color: #363636;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }

  .filter-container {
    .search {
      background-color: #fff;
      border-radius: 5px;
      border: 1px solid #e0e0e0;
      display: flex;
      align-items: center;
      padding: 8px 10px;

      .search-box__input {
        border: none;
        outline: none;
        min-width: 220px;
        font-family: 'Exo 2', sans-serif;
        font-size: 14px;
        color: #363636;
        background: transparent;

        &::placeholder {
          color: #7a7a7a;
        }
      }

      .search-box__icon {
        width: 16px;
        height: 16px;
        margin-left: 8px;
        flex-shrink: 0;
      }
    }
  }

  .status-badge {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    display: block;
    width: fit-content;
    background: #f5f5f5;
    color: #6f6f6f;
    border: 1px solid #d9d9d9;

    &.status-completed,
    &.status-progress,
    &.status-default {
      background: #f5f5f5;
      color: #6f6f6f;
      border: 1px solid #d9d9d9;
    }
  }

  .final-result-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    .final-result-icon {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #fff;
      position: relative;
      flex-shrink: 0;

      .final-result-symbol {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
        font-family: 'Exo 2', sans-serif;
        text-align: center;
        height: inherit;
      }
    }

    &.final-result-positive {
      background: #be2f48;
      color: #fff;

      .final-result-symbol {
        color: #be2f48;
      }
    }

    &.final-result-negative {
      background: #2ea65a;
      color: #fff;

      .final-result-symbol {
        color: #2ea65a;
      }
    }

    &.final-result-default {
      background: #e5e7eb;
      color: #4b5563;

      .final-result-symbol {
        color: #4b5563;
      }
    }
  }

  .final-result-empty {
    color: #999;
    font-size: 14px;
    font-weight: 600;
  }

  .viewed-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 0;
    background: #eef3f8;
    border: 1px solid #d7e1ec;
    color: #5f6b7a;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1px;
    padding: 0px 5px;
    line-height: 1.2;
  }

  .viewed-badge-icon {
    font-size: 13px !important;
    color: #5f6b7a;
    width: 13px;
    height: 13px;
    min-width: 13px;
  }

  :deep(.data-card-table .table-body .card-row .card-header) {
    min-height: 74px;
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .priority-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
    max-height: 2.4em;
  }

  .expanded-details {
    .exam-info-section {
      margin-bottom: 20px;

      .exam-info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;
        align-items: start;

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .label {
            font-size: 11px;
            color: #999;
            font-weight: 500;
            text-transform: uppercase;
          }

          .value {
            font-size: 14px;
            color: #363636;
            font-weight: 500;
          }

          &.download-section {
            justify-content: center;
            align-items: center;
            flex-direction: row;
            gap: 8px;

            .btn-inline {
              min-width: 50px;

              i {
                width: 20px;
                height: 20px;
                min-width: 20px;
                margin: 0;
                padding: 0;

                :deep(path) {
                  fill: white;
                }
              }

              .traceability {
                width: 28px;
                height: 28px;
                margin: 0;
                padding-top: 4px;
              }
            }
          }
        }
      }
    }
  }
}
@media (max-width: 768px) {
  .microbiology-results {
    .expanded-details .exam-info-section .exam-info-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }
}
</style>
